import { Socket, io } from 'socket.io-client';

interface GameResponse {
  error?: string;
  success?: boolean;
}

interface GameAvailabilityResponse extends GameResponse {
  canBecomeLeader: boolean;
}

interface GameStatusResponse extends GameResponse {
  phase: string;
  leaderName: string;
  players: string[];
  joinedPlayers: string[];
  readyPlayers: string[];
  setupComplete: boolean;
}

interface SetupPlayersRequest {
  leaderName: string;
  players: string[];
}

interface JoinGameResponse extends GameResponse {
  success: boolean;
}

class SocketService {
  private socket: Socket | null = null;
  private static instance: SocketService;

  private constructor() {}

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  connect(): Socket {
    if (!this.socket) {
      this.socket = io('https://knock-server-yv8c.onrender.com');
      console.log('Creating new socket connection...');
      
      this.socket.on('connect', () => {
        console.log('Socket connected successfully');
      });

      this.socket.on('disconnect', () => {
        console.log('Socket disconnected');
      });

      this.socket.on('connect_error', (error: any) => {
        console.error('Socket connection error:', error);
      });
    }
    return this.socket;
  }

  async checkGameAvailability(): Promise<GameAvailabilityResponse> {
    return new Promise((resolve, reject) => {
      const socket = this.connect();
      console.log('Emitting checkGameAvailability...');
      
      const timeout = setTimeout(() => {
        console.error('checkGameAvailability timeout');
        reject(new Error('Request timeout'));
      }, 10000);

      socket.emit('checkGameAvailability', {}, (response: GameAvailabilityResponse) => {
        clearTimeout(timeout);
        console.log('checkGameAvailability response:', response);
        resolve(response);
      });
    });
  }

  async checkGameStatus(): Promise<GameStatusResponse> {
    return new Promise((resolve, reject) => {
      const socket = this.connect();
      console.log('Emitting checkGameStatus...');
      
      const timeout = setTimeout(() => {
        console.error('checkGameStatus timeout');
        reject(new Error('Request timeout'));
      }, 10000);

      socket.emit('checkGameStatus', {}, (response: GameStatusResponse) => {
        clearTimeout(timeout);
        console.log('checkGameStatus response:', response);
        resolve(response);
      });
    });
  }

  async setupPlayers(setup: SetupPlayersRequest): Promise<GameResponse> {
    console.log('Setting up players:', setup);
    return new Promise((resolve, reject) => {
      const socket = this.connect();
      
      const timeout = setTimeout(() => {
        console.error('setupPlayers timeout');
        reject(new Error('Request timeout'));
      }, 10000);

      socket.emit('setupPlayers', setup, (response: GameResponse) => {
        clearTimeout(timeout);
        console.log('Setup response:', response);
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });
    });
  }

  async joinGame(name: string): Promise<JoinGameResponse> {
    console.log('Joining game as:', name);
    return new Promise((resolve, reject) => {
      const socket = this.connect();
      
      const timeout = setTimeout(() => {
        console.error('joinGame timeout');
        reject(new Error('Request timeout'));
      }, 10000);

      socket.emit('joinGame', { name }, (response: JoinGameResponse) => {
        clearTimeout(timeout);
        console.log('Join game response:', response);
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });
    });
  }

  async startGame(): Promise<GameResponse> {
    return new Promise((resolve, reject) => {
      const socket = this.connect();
      
      const timeout = setTimeout(() => {
        console.error('startGame timeout');
        reject(new Error('Request timeout'));
      }, 10000);

      socket.emit('startGame', {}, (response: GameResponse) => {
        clearTimeout(timeout);
        console.log('startGame response:', response);
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

export const socketService = SocketService.getInstance(); 